import { useEffect, useState } from "react";
import { Shield, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const UsersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);

  const load = () =>
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const toggleRole = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: u.role === "admin" ? "user" : "admin" });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}? Verranno rimossi anche calendari e ordini.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!users) {
    return <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-amber-500/20" data-testid="users-tab">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-amber-500/15 bg-[#0C241A]/70 text-xs uppercase tracking-wider text-amber-300/80">
            <th className="px-5 py-3.5">Utente</th>
            <th className="px-5 py-3.5">Ruolo</th>
            <th className="px-5 py-3.5">Calendari</th>
            <th className="px-5 py-3.5">Ordini</th>
            <th className="px-5 py-3.5">Iscritto il</th>
            <th className="px-5 py-3.5 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-amber-500/10 text-emerald-100/85">
              <td className="px-5 py-3.5">
                <p className="font-semibold text-amber-100">{u.name}</p>
                <p className="text-xs text-emerald-100/50">{u.email}</p>
              </td>
              <td className="px-5 py-3.5">
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${u.role === "admin" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/10 text-emerald-300"}`}>
                  {u.role}
                </span>
              </td>
              <td className="px-5 py-3.5">{u.calendars}</td>
              <td className="px-5 py-3.5">{u.orders}</td>
              <td className="px-5 py-3.5 text-emerald-100/60">{new Date(u.created_at).toLocaleDateString("it-IT")}</td>
              <td className="px-5 py-3.5">
                {u.id !== me.id && (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      data-testid={`user-role-${u.email}`}
                      onClick={() => toggleRole(u)}
                      className="rounded-lg border border-amber-400/40 p-2 text-amber-200 hover:bg-amber-400/10"
                      aria-label="Cambia ruolo"
                    >
                      {u.role === "admin" ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      data-testid={`user-delete-${u.email}`}
                      onClick={() => remove(u)}
                      className="rounded-lg border border-red-400/40 p-2 text-red-300 hover:bg-red-400/10"
                      aria-label="Elimina"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
